import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addDays, formatTrDate, isValidLocalDate, parseTrDate, toIstanbulDate, } from "@station/core";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover } from "radix-ui";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/button.js";
import { Input } from "../components/form.js";
import { cn } from "../lib/cn.js";
const MONTHS = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
];
const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const WEEKDAYS_LONG = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
/** "18092026", "18.9.2026", "18/09/2026" → "2026-09-18". Geçersizse null. */
export function parseDateInput(text) {
    const trimmed = text.trim();
    if (/^\d{8}$/.test(trimmed))
        return parseTrDate(`${trimmed.slice(0, 2)}.${trimmed.slice(2, 4)}.${trimmed.slice(4)}`);
    return parseTrDate(trimmed.replace(/[/-]/g, "."));
}
/** Ayın takvim ızgarası: Pazartesi ile başlar, 6 hafta × 7 gün. */
export function monthGrid(year, monthIndex) {
    const first = `${String(year).padStart(4, "0")}-${String(monthIndex + 1).padStart(2, "0")}-01`;
    const weekday = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7; // Pazartesi = 0
    return Array.from({ length: 42 }, (_, index) => addDays(first, index - weekday));
}
function Calendar({ selected, onSelect, min, max }) {
    const today = toIstanbulDate(new Date());
    const anchor = selected ?? today;
    const [view, setView] = useState({
        year: Number(anchor.slice(0, 4)),
        month: Number(anchor.slice(5, 7)) - 1,
    });
    const [focusDate, setFocusDate] = useState(anchor);
    const days = useMemo(() => monthGrid(view.year, view.month), [view]);
    const move = (date) => {
        setFocusDate(date);
        setView({ year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) - 1 });
    };
    const shiftMonth = (delta) => {
        const next = new Date(Date.UTC(view.year, view.month + delta, 1));
        setView({ year: next.getUTCFullYear(), month: next.getUTCMonth() });
    };
    const disabled = (date) => (min !== undefined && date < min) || (max !== undefined && date > max);
    return (_jsxs("div", { className: "w-72 p-3", children: [_jsxs("div", { className: "mb-2 flex items-center justify-between", children: [_jsx(Button, { variant: "ghost", size: "icon", className: "size-8", onClick: () => shiftMonth(-1), "aria-label": "\u00D6nceki ay", children: _jsx(ChevronLeft, { "aria-hidden": "true" }) }), _jsxs("p", { className: "text-sm font-semibold", "aria-live": "polite", children: [MONTHS[view.month], " ", view.year] }), _jsx(Button, { variant: "ghost", size: "icon", className: "size-8", onClick: () => shiftMonth(1), "aria-label": "Sonraki ay", children: _jsx(ChevronRight, { "aria-hidden": "true" }) })] }), _jsxs("div", { role: "grid", "aria-label": `${MONTHS[view.month]} ${view.year}`, className: "grid grid-cols-7 gap-0.5 text-center text-sm", onKeyDown: (event) => {
                    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
                    if (step === undefined)
                        return;
                    event.preventDefault();
                    move(addDays(focusDate, step));
                }, children: [WEEKDAYS.map((day, index) => (_jsx("span", { role: "columnheader", "aria-label": WEEKDAYS_LONG[index], className: "py-1 text-xs font-medium text-muted-foreground", children: day }, day))), days.map((date) => {
                        const outside = Number(date.slice(5, 7)) - 1 !== view.month;
                        const isSelected = date === selected;
                        return (_jsx("button", { type: "button", role: "gridcell", "aria-selected": isSelected, "aria-label": formatTrDate(date), "aria-current": date === today ? "date" : undefined, tabIndex: date === focusDate ? 0 : -1, ref: (element) => {
                                if (element &&
                                    date === focusDate &&
                                    element.ownerDocument.activeElement?.getAttribute("role") === "gridcell")
                                    element.focus();
                            }, disabled: disabled(date), onClick: () => onSelect(date), className: cn("size-9 rounded-md hover:bg-muted disabled:pointer-events-none disabled:opacity-30", outside && "text-muted-foreground/70", date === today && "font-bold underline underline-offset-4", isSelected && "bg-primary text-primary-foreground hover:bg-primary"), children: Number(date.slice(8, 10)) }, date));
                    })] })] }));
}
/** Tarih girişi — Türkiye biçimi (GG.AA.YYYY). Elle yazılabilir ("18092026" da olur) ya da takvimden seçilir. */
export function DateInput({ value, onValueChange, onParseError, min, max, className, onBlur, ...props }) {
    const current = value && isValidLocalDate(value) ? value : null;
    const [text, setText] = useState(current ? formatTrDate(current) : "");
    const [open, setOpen] = useState(false);
    const [invalid, setInvalid] = useState(false);
    useEffect(() => setText(current ? formatTrDate(current) : ""), [current]);
    function commit(raw) {
        if (raw.trim() === "") {
            setInvalid(false);
            onParseError?.(null);
            onValueChange(null);
            return;
        }
        const parsed = parseDateInput(raw);
        const outOfRange = parsed !== null && ((min !== undefined && parsed < min) || (max !== undefined && parsed > max));
        setInvalid(parsed === null || outOfRange);
        onParseError?.(parsed === null
            ? "Geçerli bir tarih girin (GG.AA.YYYY)"
            : outOfRange
                ? "Tarih izin verilen aralığın dışında"
                : null);
        if (parsed && !outOfRange) {
            onValueChange(parsed);
            setText(formatTrDate(parsed));
        }
    }
    return (_jsxs(Popover.Root, { open: open, onOpenChange: setOpen, children: [_jsxs("div", { className: "relative", children: [_jsx(Input, { ...props, inputMode: "numeric", autoComplete: "off", placeholder: props.placeholder ?? "GG.AA.YYYY", value: text, "aria-invalid": invalid || props["aria-invalid"], className: cn("tabular pr-10", className), onChange: (event) => setText(event.target.value), onBlur: (event) => {
                            commit(text);
                            onBlur?.(event);
                        } }), _jsx(Popover.Trigger, { asChild: true, children: _jsx("button", { type: "button", className: "absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground", "aria-label": "Takvimi a\u00E7", children: _jsx(CalendarDays, { className: "size-4", "aria-hidden": "true" }) }) })] }), _jsx(Popover.Portal, { children: _jsx(Popover.Content, { align: "end", sideOffset: 6, className: "z-50 rounded-md border bg-card shadow-lg", children: _jsx(Calendar, { selected: current, min: min, max: max, onSelect: (date) => {
                            setInvalid(false);
                            onParseError?.(null);
                            onValueChange(date);
                            setOpen(false);
                        } }) }) })] }));
}
export const DATE_RANGE_PRESETS = [
    { key: "today", label: "Bugün" },
    { key: "yesterday", label: "Dün" },
    { key: "last7", label: "Son 7 gün" },
    { key: "thisMonth", label: "Bu ay" },
    { key: "lastMonth", label: "Geçen ay" },
    { key: "thisYear", label: "Bu yıl" },
];
/** Hazır aralıklar İstanbul takvim gününe göre hesaplanır. */
export function resolvePreset(preset, today = toIstanbulDate(new Date())) {
    const [year, month] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
    const iso = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
    switch (preset) {
        case "today":
            return { from: today, to: today };
        case "yesterday":
            return { from: addDays(today, -1), to: addDays(today, -1) };
        case "last7":
            return { from: addDays(today, -6), to: today };
        case "thisMonth":
            return { from: iso(year, month, 1), to: today };
        case "lastMonth":
            return { from: iso(year, month - 1, 1), to: iso(year, month, 0) };
        case "thisYear":
            return { from: iso(year, 1, 1), to: today };
    }
}
export function DateRangePicker({ value, onValueChange, className, label = "Tarih", }) {
    const invalidOrder = value.from !== null && value.to !== null && value.from > value.to;
    return (_jsxs("div", { className: cn("grid gap-2", className), children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(DateInput, { "aria-label": `${label} başlangıç`, value: value.from, onValueChange: (from) => onValueChange({ ...value, from }), ...(value.to ? { max: value.to } : {}), className: "w-40" }), _jsx("span", { "aria-hidden": "true", className: "text-muted-foreground", children: "\u2013" }), _jsx(DateInput, { "aria-label": `${label} bitiş`, value: value.to, onValueChange: (to) => onValueChange({ ...value, to }), ...(value.from ? { min: value.from } : {}), className: "w-40" })] }), _jsx("div", { className: "flex flex-wrap gap-1", role: "group", "aria-label": "Haz\u0131r aral\u0131klar", children: DATE_RANGE_PRESETS.map((preset) => (_jsx(Button, { variant: "ghost", size: "sm", onClick: () => onValueChange(resolvePreset(preset.key)), children: preset.label }, preset.key))) }), invalidOrder ? (_jsx("p", { role: "alert", className: "text-xs font-medium text-destructive", children: "Ba\u015Flang\u0131\u00E7 tarihi biti\u015Ften sonra olamaz" })) : null] }));
}
//# sourceMappingURL=date-inputs.js.map